import { useEffect, useRef, useState } from 'react'
import Sheet from './Sheet'
import { registerImageCropOpener, type ImageCropRequest } from '../utils/imageCropRequest'
import { centerCrop, type CropRect } from '../pages/generate/utils/imageInput'

/**
 * Окно «Фото вытянутое — выберите, что оставить» (правило 5 подгонки фото, владелец 29.09): фото вытянутее,
 * чем берёт нейросеть, и срезать нужно больше 10 % — сами не режем, чтобы не отрезать голову или человека
 * у края. Рамка — на всю короткую сторону, длинная — по пропорции модели (самая маленькая обрезка);
 * клиент двигает её мышью, пальцем или стрелками. Одна копия в App; места загрузки зовут requestImageCrop.
 */
export default function ImageCropHost() {
	const [open, setOpen] = useState(false)
	// Запрос живёт и во время анимации закрытия — иначе окно на 0,3 с опустело бы.
	const [view, setView] = useState<ImageCropRequest | null>(null)
	const pending = useRef<((rect: CropRect | null) => void) | null>(null)

	useEffect(() => {
		registerImageCropOpener((req, resolve) => {
			// Новый запрос при открытом окне (не должно случаться: загрузки идут по очереди) — прежний отменён.
			pending.current?.(null)
			pending.current = resolve
			setView(req)
			setOpen(true)
		})
		return () => {
			registerImageCropOpener(null)
			pending.current?.(null)
			pending.current = null
		}
	}, [])

	const finish = (rect: CropRect | null) => {
		const resolve = pending.current
		pending.current = null
		setOpen(false)
		resolve?.(rect)
	}

	return (
		<Sheet open={open} onClose={() => finish(null)} variant="center" title="Фото вытянутое — выберите, что оставить">
			{view && <ImageCropper key={view.url} req={view} onDone={finish} />}
		</Sheet>
	)
}

function ImageCropper({ req, onDone }: { req: ImageCropRequest; onDone: (rect: CropRect | null) => void }) {
	const landscape = req.width >= req.height
	const frame = centerCrop(req.width, req.height, req.ratio)
	const long = landscape ? req.width : req.height
	const frameLong = landscape ? frame.w : frame.h
	const range = Math.max(0, long - frameLong)
	const [offset, setOffset] = useState(landscape ? frame.x : frame.y)
	const stageRef = useRef<HTMLDivElement>(null)
	const frameRef = useRef<HTMLDivElement>(null)
	// Фокус сразу на рамке: стрелки двигают её без лишнего Tab.
	useEffect(() => {
		frameRef.current?.focus({ preventScroll: true })
	}, [])
	const drag = useRef<{ pointer: number; from: number; pxPerSource: number; start: number } | null>(null)

	const clamp = (v: number) => Math.max(0, Math.min(range, Math.round(v)))
	const pct = (v: number) => `${(v / long) * 100}%`
	const ratioLabel = String(req.ratio).replace('.', ',')

	const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		const stage = stageRef.current
		if (!stage) return
		const box = stage.getBoundingClientRect()
		const displayLong = landscape ? box.width : box.height
		drag.current = { pointer: landscape ? e.clientX : e.clientY, from: offset, pxPerSource: displayLong / long, start: e.pointerId }
		e.currentTarget.setPointerCapture(e.pointerId)
	}
	const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		const d = drag.current
		if (!d || d.start !== e.pointerId) return
		const delta = ((landscape ? e.clientX : e.clientY) - d.pointer) / d.pxPerSource
		setOffset(clamp(d.from + delta))
	}
	const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
		if (drag.current?.start === e.pointerId) drag.current = null
	}
	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		const step = Math.max(1, Math.round(range / 20))
		const back = landscape ? 'ArrowLeft' : 'ArrowUp'
		const fwd = landscape ? 'ArrowRight' : 'ArrowDown'
		if (e.key === back) setOffset((o) => clamp(o - step))
		else if (e.key === fwd) setOffset((o) => clamp(o + step))
		else if (e.key === 'Home') setOffset(0)
		else if (e.key === 'End') setOffset(range)
		else return
		e.preventDefault()
	}

	return (
		<div className="image-crop">
			<p className="image-crop__lead">
				Эта нейросеть берёт фото не вытянутее {ratioLabel}:1. Сдвиньте рамку на главное — лишнее по краям обрежем,
				сама картинка не растянется.
			</p>
			<div className="image-crop__stage" ref={stageRef}>
				<img src={req.url} alt="" className="image-crop__img" draggable={false} />
				<div
					ref={frameRef}
					className={`image-crop__frame image-crop__frame--${landscape ? 'x' : 'y'}`}
					style={landscape ? { left: pct(offset), width: pct(frameLong) } : { top: pct(offset), height: pct(frameLong) }}
					role="slider"
					tabIndex={0}
					aria-label="Положение рамки"
					aria-orientation={landscape ? 'horizontal' : 'vertical'}
					aria-valuemin={0}
					aria-valuemax={range}
					aria-valuenow={offset}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerUp}
					onPointerCancel={onPointerUp}
					onKeyDown={onKeyDown}
				>
					<span className="image-crop__grip" aria-hidden="true" />
				</div>
			</div>
			<div className="image-crop__actions">
				<button type="button" className="suno-sheet-btn suno-sheet-btn--soft" onClick={() => onDone(null)}>
					Отмена
				</button>
				<button
					type="button"
					className="suno-sheet-btn suno-sheet-btn--primary"
					onClick={() =>
						onDone(landscape ? { x: offset, y: 0, w: frame.w, h: frame.h } : { x: 0, y: offset, w: frame.w, h: frame.h })
					}
				>
					Обрезать
				</button>
			</div>
		</div>
	)
}
